// Chakra Imports
import {
  Box,
  Text,
  Flex,
  HStack,
  VStack,
  Select,
  Input,
  Avatar,
  Spinner,
  useDisclosure,
} from "components-lib";
import React, { useState, useEffect, useCallback } from "react";
import ChatService from "services/chatService";
import ChatModal from "components/chat/ChatModal";
import { useChat } from "contexts/ChatContext"; // Import Context

// Configuration constants
const REFRESH_INTERVAL_MS = 300000; // 5 minutes - change this to modify refresh frequency

/**
 * Frontdesk Alerts Component
 * Displays frontdesk messages with filtering capabilities
 */
export default function FrontdeskAlerts({ 
  clinicOptions = [], 
  frontdeskOptions = [], 
  selectedClinic = 'all',
  onClinicChange = () => {},
  refreshTrigger = 0
}) {
  // State management
  const { chatState, syncMessages, openChat } = useChat(); // Use Context
  const [frontdeskAlerts, setFrontdeskAlerts] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [individualUnreadCounts, setIndividualUnreadCounts] = useState({});
  // history UI removed here — we only open chat modal on click
  
  // Filter states specific to frontdesk alerts
  const [selectedFrontdesk, setSelectedFrontdesk] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');

  // Chat modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Ref to track when a conversation was last read locally to persist "seen" state across refetches
  const lastReadTimestamps = React.useRef({});

  // Filter frontdesk options based on selected clinic
  const filteredFrontdeskOptions = frontdeskOptions

  // Fetch frontdesk alerts from participants API
  const fetchFrontdeskAlerts = useCallback(async () => {
    setLoadingParticipants(true);
    try {
      // Fetch participants who have messaged admin
      const participants = await ChatService.getAdminParticipants(1, 'admin');

      // Normalize participants into an array (support {success,data} and raw array)
      const participantsArray = Array.isArray(participants)
        ? participants
        : (participants && (participants.data || participants.data?.data)) || [];

      console.log('[FrontdeskAlerts] fetchFrontdeskAlerts - participants raw:', participants);
      console.log('[FrontdeskAlerts] fetchFrontdeskAlerts - normalized participantsArray length:', participantsArray.length);

      const frontdesk = [];
      const unreadCounts = {};

      // Build allowed frontdesk ids when a clinic is selected.
      const allowedFrontdeskIds = selectedClinic && `${selectedClinic}` !== 'all'
        ? filteredFrontdeskOptions
            .map(fd => fd.id)
            .filter(id => id !== undefined && id !== null)
            .map(id => `${id}`)
        : null;

      console.log('[FrontdeskAlerts] selectedClinic:', selectedClinic);
      console.log('[FrontdeskAlerts] filteredFrontdeskOptions:', filteredFrontdeskOptions);
      console.log('[FrontdeskAlerts] allowedFrontdeskIds:', allowedFrontdeskIds);

      if (Array.isArray(participantsArray)) {
        for (const participant of participantsArray) {
          // Accept either participant.participant_type or participant.user_type
          const isFrontdesk = (participant.participant_type === 'front_desk' || participant.user_type === 'front_desk');
          // Ensure participant has an id/name
          const participantId = participant.user_id !== undefined && participant.user_id !== null
            ? `${participant.user_id}`
            : (participant.id !== undefined && participant.id !== null ? `${participant.id}` : null);

          console.log('[FrontdeskAlerts] processing participant:', participant);

          if (!isFrontdesk) {
            console.log('[FrontdeskAlerts] skipping participant (not frontdesk):', participantId);
            continue;
          }

          if (!participantId) {
            console.log('[FrontdeskAlerts] skipping participant (no user id):', participant);
            continue;
          }

          // NEW: only filter participants by frontdeskOptions when a clinic is selected.
          // When 'all' clinics are selected we should include any frontdesk participant returned by the API.
          const existsInFrontdeskOptions = frontdeskOptions.some(fd => {
            const fdId = fd.id !== undefined && fd.id !== null ? `${fd.id}` : null;
            const fdUserId = fd.user_id !== undefined && fd.user_id !== null ? `${fd.user_id}` : null;
            return fdId === participantId || fdUserId === participantId;
          });

          if (selectedClinic !== 'all' && !existsInFrontdeskOptions) {
            console.log('[FrontdeskAlerts] skipping participant (not found in frontdeskOptions for selected clinic):', participantId);
            continue;
          }

          // If clinic filter is active, only include participants whose id exists in filtered frontdesk options
          if (allowedFrontdeskIds && !allowedFrontdeskIds.includes(participantId)) {
            // However, additionally check frontdeskOptions to see if participant exists there with matching clinic
            const fdMatch = frontdeskOptions.find(fd => `${fd.id}` === participantId);
            if (fdMatch) {
              console.log('[FrontdeskAlerts] participant id exists in frontdeskOptions but not in allowedFrontdeskIds (clinic mismatch). fdMatch:', fdMatch);
            } else {
              console.log('[FrontdeskAlerts] participant id not in allowedFrontdeskIds and not in frontdeskOptions => skipping:', participantId);
              continue;
            }
            // If fdMatch exists, but clinic mismatch, depending on desired behavior we can still include or skip.
            // Based on request, when a clinic is selected if user id of that participant exist in frontdeskOptions and user_type is front_desk add in frontdesk alert list
            // So if fdMatch exists (regardless of its clinic), include it — but we'll log the clinic for debugging.
          }

          const participantType = 'front_desk';
          const participantName = participant.user_name || participant.name || 'Frontdesk';

          // Single history fetch to derive latest message and unread count
          let latestMessageDate = new Date().toISOString().split('T')[0];
          let rawLatestDate = new Date().toISOString(); // Default to now
          let latestMessage = 'New message from frontdesk';
          let unreadFromParticipant = 0;

          try {
            const historyResponse = await ChatService.getMessageHistory(
              participantId,
              participantType,
              1,
              'admin'
            );

            const history = Array.isArray(historyResponse) ? historyResponse : (historyResponse.data || []);

            console.log(`[FrontdeskAlerts] history for participant ${participantId}:`, history);

            if (Array.isArray(history) && history.length > 0) {
              const sortedHistory = [...history].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

              // 1. Latest activity for sorting
              const latestOverall = sortedHistory[0];
              rawLatestDate = latestOverall.created_at;
              latestMessageDate = new Date(rawLatestDate).toISOString().split('T')[0];

              // 2. Latest message from Frontdesk for display
              // Use loose equality
              const latestFromFD = sortedHistory.find(msg => msg.sender_id == participantId);

              if (latestFromFD) {
                latestMessage = latestFromFD.message && latestFromFD.message.length > 50
                  ? latestFromFD.message.substring(0, 50) + '...'
                  : (latestFromFD.message || 'Attachment');
              } else {
                latestMessage = 'No message from frontdesk';
              }

              // Filter unread messages: must be unread on server AND newer than our local last read timestamp
              const unreadKey = `${participantType}_${participantId}`;
              const lastReadTime = lastReadTimestamps.current[unreadKey] || 0;

              unreadFromParticipant = history.filter(msg => {
                const isUnreadServer = !msg.is_read;
                const msgTime = new Date(msg.created_at).getTime();
                // Note: Use loose equality for sender_id just in case
                const isSender = msg.sender_id == participantId;

                return isSender && isUnreadServer && msgTime > lastReadTime;
              }).length;

              // SYNC TO STORE
              syncMessages(participantId, history);

            }
          } catch (error) {
            console.warn(`Error fetching message history for frontdesk ${participantName} (${participantId}):`, error);
          }

          // Find frontdesk's clinic information (if available). Match on fd.id only.
          const frontdeskData = frontdeskOptions.find(fd => `${fd.id}` === participantId);
          const clinicId = frontdeskData ? frontdeskData.clinic_id : null;

          console.log(`[FrontdeskAlerts] adding frontdesk alert for participantId=${participantId}, name=${participantName}, clinicId=${clinicId}, unread=${unreadFromParticipant}`);

          frontdesk.push({
            id: participant.user_id || participant.id,
            name: participant.user_name || participant.name || participantId,
            category: 'Frontdesk Message',
            message: latestMessage,
            date: latestMessageDate,
            rawDate: rawLatestDate,
            sender_type: 'front_desk',
            sender_id: participant.user_id || participant.id,
            sender_name: participant.user_name || participant.name || participantId,
            clinic_id: clinicId
          });

          unreadCounts[`${participantType}_${participantId}`] = unreadFromParticipant;
        }
      }

      // Sort frontdesk by latest activity (newest first)
      frontdesk.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));


      setFrontdeskAlerts(frontdesk);
      setIndividualUnreadCounts(unreadCounts);

    } catch (error) {
      console.error('Error fetching frontdesk alerts:', error);
      setFrontdeskAlerts([]);
      setIndividualUnreadCounts({});
    } finally {
      setLoadingParticipants(false);
    }
  }, [ selectedClinic]); // added frontdeskOptions to deps

  // Fetch data on component mount and when refresh trigger or clinic changes
  useEffect(() => {
    fetchFrontdeskAlerts();

    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchFrontdeskAlerts();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refreshTrigger, fetchFrontdeskAlerts]);

  // Filter alerts based on selected filters
  const filterAlerts = (alerts) => {
    return alerts.filter(alert => {
      // Clinic filtering
      // const clinicMatch = selectedClinic === 'all' || 
      //   (alert.clinic_id && `${alert.clinic_id}` === `${selectedClinic}`);
      
      // Date filtering
      const dateMatch = !selectedDate || alert.date === selectedDate;

      // Frontdesk selection filtering
      const frontdeskMatch = selectedFrontdesk === 'all' || `${alert.user_id}` === `${frontdeskOptions.find(fd => `${fd.id}` === selectedFrontdesk)?.id}`;
      
      return dateMatch && frontdeskMatch;
    });
  };

  // Process alerts through filters (no sorting or pagination)
  const processedAlerts = selectedClinic === 'all' ? frontdeskAlerts : filterAlerts(frontdeskAlerts);

  // Handle message click to open chat
  const handleMessageClick = (message) => {
    // Create participant object for chat modal
    const participant = {
      name: message.sender_name || 'Frontdesk',
      sender_id: message.sender_id,
      sender_type: message.sender_type,
      type: message.sender_type,
      sender_name: message.sender_name
    };

    // Mark as read locally
    // const unreadKey = `${message.sender_type}_${message.sender_id}`;

    // // Update ref to persist this read status
    // lastReadTimestamps.current[unreadKey] = Date.now();

    // setIndividualUnreadCounts(prev => ({
    //   ...prev,
    //   [unreadKey]: 0
    // }));

    openChat(message.sender_id);

    setSelectedConversation(participant);
    onOpen();
  };

  return (
    <Box bg="white" borderRadius="xl" boxShadow="lg" h="100%" p={4}>
      {/* Header with filters */}
      <VStack spacing={4} align="stretch" mb={4}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="md" fontWeight="bold">Frontdesk Alerts</Text>
          <HStack spacing={2}>
            {/* {processedAlerts.length !== frontdeskAlerts.length && (
              <Box bg="blue.500" color="white" borderRadius="full" px={2} py={1} fontSize="xs">
                {processedAlerts.length} of {frontdeskAlerts.length}
              </Box>
            )}
            {frontdeskAlerts.length > 0 && (
              <Box bg="green.500" color="white" borderRadius="full" px={2} py={1} fontSize="xs">
                {frontdeskAlerts.length} total
              </Box>
            )} */}
          </HStack>
        </Flex>

        {/* Filter Controls */}
        <HStack spacing={3} wrap="wrap">
          <Box minW="150px">
            <Text fontSize="xs" fontWeight="medium" mb={1}>Clinic</Text>
            <Select
              value={selectedClinic}
              onChange={(e) => onClinicChange(e.target.value)}
              size="sm"
            >
              <option value="all">All Clinics</option>
              {clinicOptions.map(clinic => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </option>
              ))}
            </Select>
          </Box>
          
          <Box minW="150px">
            <Text fontSize="xs" fontWeight="medium" mb={1}>Frontdesk</Text>
            <Select
              value={selectedFrontdesk}
              onChange={(e) => setSelectedFrontdesk(e.target.value)}
              size="sm"
            >
              <option value="all">All Frontdesk</option>
              {filteredFrontdeskOptions.map(fd => (
                <option key={`${fd.id}-${fd.user_id || ''}`} value={fd.id}>
                  {fd.name}
                </option>
              ))}
            </Select>
          </Box>
          
          <Box minW="150px">
            <Text fontSize="xs" fontWeight="medium" mb={1}>Date</Text>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              size="sm"
            />
          </Box>
        </HStack>
      </VStack>

      {/* Frontdesk List */}
      <Box maxH="200px" overflowY="auto" mb={4}>
        {loadingParticipants ? (
          <Flex justify="center" align="center" py={8}>
            <Text color="gray.500">Loading frontdesk...</Text>
          </Flex>
        ) : processedAlerts.length === 0 ? (
          <Flex justify="center" align="center" py={8}>
            <Text color="gray.500">
              {frontdeskAlerts.length === 0 ? 'No frontdesk found' : 'No results match your filters'}
            </Text>
          </Flex>
        ) : (
            processedAlerts.map((alert, idx) => {
              const senderId = alert.sender_id;
              const unreadCount = chatState.chats[senderId]?.unseenCount || 0;
              const hasUnread = unreadCount > 0;

            return (
              <Flex
                key={`frontdesk-${alert.id}`}
                align="center"
                p={3}
                mb={2}
                borderRadius="md"
                // border={isUnread ? "2px solid" : "none"}
                // borderBottom={!isUnread && idx !== processedAlerts.length - 1 ? "1px solid" : "none"}
                // borderColor={isUnread ? "blue.500" : "gray.100"}
                bg={hasUnread ? "blue.50" : "transparent"}
                _hover={{ bg: hasUnread ? "blue.100" : "gray.50" }}
                transition="all 0.2s"
                onClick={() => handleMessageClick(alert)}
                style={{ cursor: 'pointer' }}
              >
                <Box position="relative" mr={3}>
                  <Avatar name={alert.name} />
                  {hasUnread && (
                    <Box
                      position="absolute"
                      top="0"
                      right="0"
                      boxSize="3"
                      bg="red.500"
                      borderRadius="full"
                      border="2px solid white"
                    />
                  )}
                </Box>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight={hasUnread ? "bold" : "semibold"}>{alert.name}</Text>
                  <Text fontSize="xs" fontWeight={hasUnread ? "bold" : "normal"} color={hasUnread ? "gray.800" : "gray.500"}>{alert.message}</Text>
                </Box>
                <VStack spacing={1} align="end">
                  {hasUnread && (
                    <Box bg="red.500" color="white" borderRadius="full" px={2} py={1} fontSize="xs">
                      {unreadCount}
                    </Box>
                  )}
                  <Text fontSize="xs" color="gray.400">{alert.date}</Text>
                </VStack>
              </Flex>
            );
          })
        )}
      </Box>

      {/* Chat Modal */}
      <ChatModal
        isOpen={isOpen}
        onClose={onClose}
        participant={selectedConversation ? {
          ...selectedConversation,
          // Ensure correct receiver/sender setup for admin chat
          receiver_id: selectedConversation.sender_id,
          receiver_type: selectedConversation.sender_type
        } : null}
        adminId={1}
        adminType="admin"
      />
    </Box>
  );
}